import {
  IARA_LAUNCHER_LAYOUT,
  IARA_LAUNCHER_POSITIONS,
} from '../constants/iara-launcher-positions';
import type {
  IaraLauncherAnchor,
  IaraLauncherPosition,
} from '../models/iara-launcher-position.model';

/**
 * Aceita só nomes do conjunto conhecido. É o que protege de preferência salva por uma
 * versão antiga, JSON adulterado à mão ou nome removido numa versão futura.
 */
export function isIaraLauncherPosition(value: unknown): value is IaraLauncherPosition {
  return (
    typeof value === 'string' && (IARA_LAUNCHER_POSITIONS as readonly string[]).includes(value)
  );
}

/**
 * Próxima posição no ciclo, em ordem visual. Serve ao clique na alça: um botão cujo
 * clique não faz nada é armadilha, então clicar move para a próxima e volta ao início.
 */
export function cycleIaraLauncherPosition(current: IaraLauncherPosition): IaraLauncherPosition {
  const index = IARA_LAUNCHER_POSITIONS.indexOf(current);

  return IARA_LAUNCHER_POSITIONS[(index + 1) % IARA_LAUNCHER_POSITIONS.length];
}

/**
 * Um passo na ordem visual, SEM dar a volta. É o que as setas fazem: teleportar de um
 * extremo ao outro com uma seta desorienta.
 */
export function stepIaraLauncherPosition(
  current: IaraLauncherPosition,
  direction: -1 | 1,
): IaraLauncherPosition {
  const index = IARA_LAUNCHER_POSITIONS.indexOf(current) + direction;
  const clamped = Math.min(Math.max(index, 0), IARA_LAUNCHER_POSITIONS.length - 1);

  return IARA_LAUNCHER_POSITIONS[clamped];
}

/**
 * Y do centro da pill nesta ancoragem, em px a partir do topo do viewport.
 */
export function resolveIaraLauncherAnchorY(
  anchor: IaraLauncherAnchor,
  viewportHeight: number,
): number {
  if (anchor.from === 'top') return anchor.offset;
  if (anchor.from === 'bottom') return viewportHeight - anchor.offset;

  return viewportHeight / 2 + anchor.offset;
}

/**
 * Encaixe cujo centro está mais perto de `pointerY`.
 *
 * Puro e 1-D: as três posições compartilham a borda direita, então o eixo X não
 * contribui para distância nenhuma. Sem medir DOM, o que deixa isto testável sem layout.
 *
 * Empate resolve pela ordem de `IARA_LAUNCHER_POSITIONS` (a de cima ganha) — precisa ser
 * determinístico, senão o encaixe oscilaria no ponto médio exato.
 */
export function nearestIaraLauncherPosition(
  pointerY: number,
  viewportHeight: number,
): IaraLauncherPosition {
  let nearest: IaraLauncherPosition = IARA_LAUNCHER_POSITIONS[0];
  let shortest = Number.POSITIVE_INFINITY;

  for (const position of IARA_LAUNCHER_POSITIONS) {
    const distance = Math.abs(
      resolveIaraLauncherAnchorY(IARA_LAUNCHER_LAYOUT[position].anchor, viewportHeight) - pointerY,
    );

    if (distance < shortest) {
      shortest = distance;
      nearest = position;
    }
  }

  return nearest;
}
